<template>
  <neo-dropdown 
    ref="neoDropdown"
    class="px-2"
    placement="right"
    :disableAreaTrigger="true"
    @onOpen="onOpen"
  >
    <template #drop-trigger>
      <div class="flex flex-col w-32">
        <div class="text-base-content-300 text-sm tracking-wide pb-2">{{ revettingLabel }}</div>
        <div class="self-left">
          <UIToggle
            v-if="permissions.ro_refresh_frequency || permissions.rw_refresh_frequency"
            :selected="isRevettingOn"
            class="cursor-pointer"
            :disabled="isToggleDisabled"
            @select="toggleDropdown($event)"
          />
        </div>
      </div>
    </template>

    <template #drop-trigger-before>
      <InfoIcon
        :class="['cursor-pointer w-4 h-4', { 'revetting-info': lapsedDaysLabel }]"
        @click.stop="clickInfo"
      />
    </template>

    <template #drop-content>
      <div
        v-if="isInfoOpen"
        class="flex flex-col px-4 pt-4 pb-2"
      >
        <span class="text-base-content-300 text-sm tracking-wide pb-2">{{ refreshDueDateLabel }}</span>
        <p
          v-if="Boolean(nextRefreshDate)"
          class="text-base-content-300 text-sm tracking-wide pb-2"
        >
          <span>{{ revettingDateLabel }}</span>
          <span 
            v-if="lapsedDaysLabel" 
            class="text-red-500 font-medium"
          >
            ({{ lapsedDaysLabel }})
          </span>
        </p>
        <p v-else>--</p>
      </div>
      <FormulateForm
        v-else
        class="flex flex-col gap-6 w-60 p-4"
        #default="{ hasErrors }"
      >
        <div class="flex flex-col gap-2">
          <FormulateInput
            wrapper-class="radio-group__row"
            type="radio"
            :placeholder="$t('actions.select')"
            v-model="formData.revettingType"
            :options="revettingTypeOpt"
            :disabled="revettingFormDisabled"
            @input="resetFormDataValue('revettingType')"
          />

          <div class="flex flex-col gap-2">
            <div
              v-if="isFrequencyType"
              class="flex flex-col gap-2"
            >
              <FormulateInput
                v-model="formData.timeSpanType"
                type="select"
                :options="timeSpanTypeOpt"
                :placeholder="$t('actions.select')"
                :disabled="revettingFormDisabled"
                class="w-full"
                @input="resetFormDataValue('timeSpanType')"
              />
              <FormulateInput
                v-model="formData.timeSpanCount"
                type="select"
                :options="timeSpanCountOpt"
                :placeholder="$t('actions.select')"
                :disabled="revettingFormDisabled || !formData.timeSpanType"
                class="w-full"
                @input="resetFormDataValue('timeSpanCount')"
              />
            </div>
            <FormulateInput
              v-model="formData.timeSpanDate" 
              class="w-full"
              type="date"
              :min="minDate"
              :max="maxDate"
              :disabled="revettingFormDisabled || (isFrequencyType && !formData.timeSpanType)"
              :element-class="(context, classes) => ['flex-1 min-w-full'].concat(classes)" 
            />
          </div>
        </div>

        <Button
          class="flex-shrink-0 w-full"
          type="primary"
          button-type="submit"
          :disabled="submitDisabled || hasErrors"
          @click="submitForm"
        >
          {{ submitButtonText }}
        </Button>
      </FormulateForm>
    </template>
  </neo-dropdown>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { startOfToday, format } from 'date-fns'; 
import { cloneDeep, isEqual } from "lodash";
import permissionsMixin from "@shared/modules/versions/mixins/permissions";
import { formatUtcDate, MMM_d_yyyy, FORMULATE_DATE_FORMAT } from "@shared/utils/dateTime";
import NeoDropdown from '@shared/components/neo-dropdown';
import Button from "@shared/components/button";
const InfoIcon = () => import("@shared/assets/lightray/icons/ico-info.svg");

import {
  formatTimeSpanDate,
  formatDate,
  addYearsToDate,
  addMonthsToDate,
  getDiffInCalendarDays,
  validateTimeSpanDate,
} from "./utils";
import {
  YEAR,
  MONTH,
  timeSpanTypeOpt,
  REVET_ONE,
  REVET_FREQ,
  revettingTypeOpt,
} from "./constants";

export default {
  name: 'CaseRevetting',

  mixins: [permissionsMixin],

  components: {
    NeoDropdown,
    Button,
    InfoIcon,
  },

  data() {
    return {
      isInfoOpen: false,
      isRevettingOn: false,
      initialFormData: null,
      isSubmitted: false,
      formData: {
        revettingType: REVET_FREQ,
        timeSpanType: null,
        timeSpanCount: null,
        timeSpanDate: null,
      },
      revettingTypeOptMap: {
        REVET_ONE,
        REVET_FREQ,
      },
    };
  },

  async mounted() {
    this.initFormData();
  },

  computed: {
    ...mapGetters({
      refreshConfig: "VersionsStore/getRefreshConfig",
      nextRefreshDate: "VersionsStore/getNextRefreshDate",
      isRefreshConfigCreated: "VersionsStore/getIsRefreshConfigCreated",
    }),

    caseId() {
      return this.$route.query.caseid;
    },

    clientId() {
      return this.$route.query.clientId;
    },

    revettingFormDisabled() {
      return !this.permissions.rw_refresh_frequency || !this.isRevettingOn;
    },
    submitDisabled() {
      return this.revettingFormDisabled || !this.isFormValid || this.isInvalidTimeSpanDate;
    },
    isFormValid() {
      return this.isFormDataFilled(this.formData)
    },
    isToggleDisabled() {
      return !this.isFormDataFilled(this.formData);
    },
    isDirty() {
      return !isEqual(this.formData, this.initialFormData) || this.refreshConfig.active !== this.isRevettingOn;
    },
    isFrequencyType() {
      return this.formData.revettingType === this.revettingTypeOptMap.REVET_FREQ;
    },
    minDate() {
      return format(startOfToday(), FORMULATE_DATE_FORMAT);
    },
    maxDate() {
      if (!this.isFrequencyType) return;

      const { timeSpanType, timeSpanCount } = this.formData;

      if (!timeSpanType || !timeSpanCount) {
        return "";
      }

      const baseDate = startOfToday();

      let date = formatDate(baseDate);
      if (timeSpanType === YEAR) {
        date = addYearsToDate(baseDate, timeSpanCount);
      } else if (timeSpanType === MONTH) {
        date = addMonthsToDate(baseDate, timeSpanCount);
      }

      return date;
    },
    revettingDate() {
      return new Date(this.formData.timeSpanDate || this.minDate);
    },
    lapsedDays() {
      if (!this.nextRefreshDate) return null;
      const lapsedDays = getDiffInCalendarDays(this.nextRefreshDate)
      return lapsedDays;
    },
    // LABELS
    lapsedDaysLabel() {
      if (this.lapsedDays <= 0) return;
      return this.$t('components.revetting.days_lapsed', { count: this.lapsedDays });
    },
    revettingDateLabel() {
      return formatUtcDate(this.nextRefreshDate, { format: MMM_d_yyyy });
    },
    refreshDueDateLabel() {
      return this.$t('components.revetting.refresh_due_date');
    }, 
    revettingLabel() {
      return this.$t('components.revetting.refresh');
    },  
    revettingTypeOpt() {
      return revettingTypeOpt.map(opt => ({ ...opt, label: this.$t(`components.revetting.${opt.tKey}`) }));
    },
    timeSpanTypeOpt() {
      return timeSpanTypeOpt.map(opt => ({ ...opt, label: this.$t(`components.revetting.${opt.tKey}`) }));
    },
    timeSpanCountOpt() {
      if (!this.formData.timeSpanType) return [];
      const { maxCount } = timeSpanTypeOpt.find(x=> x.value == this.formData.timeSpanType);
      return Array(maxCount).fill(false).map((i, ind) => {
        const optVal = ind + 1;
        return { label: optVal, value: optVal };
      });
    },
    isInvalidTimeSpanDate() {
      return validateTimeSpanDate(this.formData.timeSpanDate, this.formData.timeSpanType);
    },
    submitButtonText() {
      if (this.isDirty || !this.isRevettingOn) return this.$t('actions.save');
      return this.isRefreshConfigCreated
        ? this.$t('components.revetting.start_refresh')
        : this.$t('actions.save');
    },

    payload() {
      const basePayload = {
        case_id: this.caseId,
        active: this.isRevettingOn,
        mode: this.formData.revettingType?.toUpperCase(),
      };

      return this.isFrequencyType ? {
        ...basePayload,
        interval_type: this.formData.timeSpanType?.toUpperCase(),
        interval_value: +this.formData.timeSpanCount,
        start_date: this.formData.timeSpanDate
      } : {
        ...basePayload,
        one_off_date: this.formData.timeSpanDate,
      }
    },

    startRefreshPermissionsErrorMsg() {
      return !this.permissions.start_refresh
        ? "components.revetting.errors.start_refresh"
        : "components.revetting.errors.allow_refresh_configuration";
    },

    configSuccessMsg() {
      return this.isRefreshConfigCreated
        ? "components.revetting.success.refresh_config_updated"
        : "components.revetting.success.refresh_config_created";
    },
  },

  watch: {
    async isRevettingOn(val) {
      this.isSubmitted = false;
      if (this.isRefreshConfigCreated && !val) {
        const response = await this.updateRefreshConfiguration(this.payload);
        this.isSubmitted = true;

        if (response) {
          await this.refetchConfig();
          this.initFormData();
        }
      }
    },

    "formData.revettingType"() {
      this.isSubmitted = false;
    },
  },

  methods: {
    ...mapActions({
      createRefreshConfiguration: "VersionsStore/createRefreshConfiguration",
      updateRefreshConfiguration: "VersionsStore/updateRefreshConfiguration",
      fetchRefreshConfiguration: "VersionsStore/fetchRefreshConfiguration",
      fetchNextRefreshDate: "VersionsStore/fetchNextRefreshDate",
      startRevetting: "VersionsStore/startRevetting",
    }),

    initFormData() {
      if (this.isRefreshConfigCreated) {
        const { active, mode, interval_type, interval_value, start_date, one_off_date } = this.refreshConfig;
        const dateTime = mode === "FREQUENCY" ? start_date : one_off_date;

        this.isRevettingOn = active;
        this.formData.revettingType = mode.toLowerCase();
        this.formData.timeSpanType = interval_type.toLowerCase();
        this.formData.timeSpanCount = interval_value;

        if (dateTime) {
          this.formData.timeSpanDate = formatTimeSpanDate(dateTime);
        }

        this.initialFormData = cloneDeep(this.formData);
      }
    },

    clickInfo() {
      this.isInfoOpen = !this.isInfoOpen;
      this.$refs.neoDropdown.isDropdownActive = this.isInfoOpen;
    }, 
    onOpen(val) {
      if (!val) {
        this.restoreFormValues();
      } else {
        this.isInfoOpen = false;
      }
    }, 
    isFormDataFilled(formData) { // check whether form data is valid for selected option one|frequency
      if (!formData) return false;
      return !!(this.isFrequencyType ? Object.values(formData).every(x => !!x) : formData.timeSpanDate)
    },
    restoreFormValues() {
      if (!this.initialFormData) return;
      this.formData = cloneDeep(this.initialFormData);
    },

    async submitForm() {
      if (!this.permissions.start_refresh || !this.permissions.allow_refresh_configuration) {
        this.$toast.error(this.$t(this.startRefreshPermissionsErrorMsg));
        return;
      }

      try {
        if (this.isRefreshConfigCreated && this.isSubmitted) {
          await this.startRevetting(this.caseId);
          this.redirectToCaseCreation();
        }

        if (this.isRefreshConfigCreated) {
          await this.updateRefreshConfiguration(this.payload);
        } else {
          await this.createRefreshConfiguration(this.payload);
        }

        await this.refetchConfig();
        this.isSubmitted = true;
        this.initFormData();
      } catch (e) {
        console.error(e);
        this.$toast.error(e.response?.data?.status_message || e?.response?.data?.detail || this.$t("general.default_error"));
        return;
      }
    },

    async refetchConfig() {
      if (!this.permissions.ro_refresh_frequency) return;

      await this.fetchRefreshConfiguration(this.caseId);
      await this.fetchNextRefreshDate(this.caseId);
    },

    redirectToCaseCreation() {
      if (!this.isRevettingOn) return;

      this.$router.push({
        name: "Create Case",
        query: {
          caseid: this.caseId,
          client: this.clientId
        },
      });
    },

    toggleDropdown() {
      if (!this.permissions.rw_refresh_frequency) {
        this.$toast.error(this.$t("components.revetting.errors.rw_refresh_frequency"));
        return;
      }

      this.$refs.neoDropdown.isDropdownActive = true;
      this.isRevettingOn = !this.isRevettingOn;
    },

    resetFormDataValue(event) {
      if (event == 'revettingType') {
        this.formData.timeSpanType = null;
        this.formData.timeSpanCount = null;
        this.formData.timeSpanDate = null;
      } else if (event === 'timeSpanType') {
        this.formData.timeSpanCount = null;
        this.formData.timeSpanDate = null;
      } else if (event === 'timeSpanCount') {
        this.formData.timeSpanDate = this.maxDate;
      }
    }
  }
}
</script>

<style>
.formulate-input {
  margin: 0
}

.formulate-input .radio-group__row  > div {
    display: flex;
    gap: 20px
}

svg.revetting-info path {
  fill: red;
}
</style>
